import { Add } from "@carbon/icons-react";
import { Breadcrumb, BreadcrumbItem, Button } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import { capitalize, toDate } from "@flaminem/utils";
import { FC } from "react";
import { Link, useParams } from "react-router-dom";

import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { usePermissions } from "src/authentication/usePermissions.ts";
import { BackButtonWithCondition } from "src/components/Buttons";
import Loading from "src/components/DataStates/Loading.tsx";
import InlineError from "src/components/Errors/InlineError.tsx";
import { ModalTransition } from "src/components/Modal";
import PageHeader from "src/components/PageHeader.tsx";
import { TabbedView } from "src/components/TabbedView/TabbedView.tsx";
import { CreateScenarioVersionModal } from "src/features/scenario/CreateVersion/CreateScenarioVersionModal.tsx";
import { useGetFullScenarioById } from "src/graphql/anti-fraud/queries/scenarios/GetFullScenarioQuery.ts";
import { useFormatDate, useT } from "src/internationalization";
import { ActiveScenarioVersionTab } from "src/pages/ScenarioDetails/ActiveScenarioVersionTab.tsx";
import { ArchivedScenarioVersionsTab } from "src/pages/ScenarioDetails/ArchivedScenarioVersionsTab.tsx";
import { DraftScenarioVersionTab } from "src/pages/ScenarioDetails/DraftScenarioVersionTab.tsx";

export const ScenarioDetailsPage: FC = () => {
  const param = useParams<{ id: string }>();

  const t = useT();
  const formatDate = useFormatDate();
  const { hasPermission } = usePermissions();
  const { caseTypeCode } = useParams<{ caseTypeCode: string }>();
  const { data: scenario, loading, error } = useGetFullScenarioById({ id: param.id });

  const createScenarioVersionModal = useModal();

  if (loading) {
    return <Loading />;
  }

  if (error || !scenario) {
    return <InlineError message={error?.message ?? ""} />;
  }

  const getTabs = () => {
    const tabs = [
      {
        code: "active",
        label: t("scenario.tab.active"),
        node: <ActiveScenarioVersionTab activeScenarioVersion={scenario.activeVersion} />,
      },
    ];

    if (scenario?.hasDraftVersion && scenario.draftVersion) {
      tabs.push({
        code: "draft",
        label: t("scenario.tab.draft"),
        node: (
          <DraftScenarioVersionTab scenarioId={scenario.id} draftVersion={scenario.draftVersion} />
        ),
      });
    }

    tabs.push({
      code: "archived",
      label: t("scenario.tab.archived"),
      node: <ArchivedScenarioVersionsTab archivedScenarioVersions={scenario.archivedVersions} />,
    });

    return tabs;
  };

  const tabs = getTabs();
  const caseName = scenario.caseType.name;

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <Breadcrumb noTrailingSlash={true}>
            <BreadcrumbItem>
              <Link to="/scenarios">{t("scenarios.title")}</Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <Link to={`/scenarios/${caseTypeCode}`}>{caseName}</Link>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage={true}>{scenario.name}</BreadcrumbItem>
          </Breadcrumb>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BackButtonWithCondition />
              <PageHeader>
                {t("scenario.title")} {scenario.name}
              </PageHeader>
            </div>

            {!scenario.hasDraftVersion &&
              hasPermission(ScopeResource.SCENARIO, Scope.CREATE, Scope.EDIT) && (
                <Button renderIcon={Add} onClick={createScenarioVersionModal.openModal}>
                  {t("scenarios.action.create.new.version")}
                </Button>
              )}
          </div>
          <div>
            <p>
              <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                {t("scenario.tab.label.date")}:{" "}
              </span>
              {formatDate(toDate(scenario.adminStatus.creation), { dateStyle: "medium" })}
            </p>
            {scenario.description && (
              <div className="line-clamp-3 text-body02 leading-body02 tracking-body02 font-body02">
                <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                  {t("general.table.description.column.title")}:{" "}
                </span>
                {capitalize(scenario.description)}
              </div>
            )}
          </div>
        </div>

        <div>
          <TabbedView
            type="contained"
            tabs={tabs}
            pathPrefix={"/scenario/:caseTypeCode/:id"}
            initialTabCode="active"
            tabPanelProps={{ className: "!p-0 !h-[calc(100vh-8rem)]" }} // to make tab be displayed at all available place
          />
        </div>
      </div>
      <ModalTransition>
        {createScenarioVersionModal.isModalOpen && (
          <CreateScenarioVersionModal
            scenario={scenario}
            onConfirm={createScenarioVersionModal.closeModal}
            onClose={createScenarioVersionModal.closeModal}
          />
        )}
      </ModalTransition>
    </>
  );
};
